/**
 * run-export.ts — przebieg eksportu widoku listy do pliku Excela (E16).
 *
 * Kolejność: pobranie wierszy widoku (serwer: te same filtry/uprawnienia co lista, wszystkie
 * strony) → kolumny pliku z ekranu → plik .xlsx (kolorowy nagłówek, obramowania, formatowanie
 * warunkowe z ustawień listy) → pobranie pliku. Nazwy `…Csv…` zostały z pierwszej wersji
 * eksportu (CSV); plik jest już .xlsx, bo CSV nie niesie obramowań ani kolorów. Funkcja jest czysta
 * względem Reacta (efekty — komunikaty i pobranie — przychodzą z zewnątrz), więc da się ją
 * testować bez drzewa komponentów; hook `useListCsvExport` dokłada toasty i stan „trwa eksport".
 */

import type { FormattingRule } from '@saas/shared';

import type { ExportColumn } from './cell-text';
import { buildXlsxBlob } from './xlsx';

/** Odpowiedź tras `…/export`: wiersze widoku + liczba wszystkich + znacznik obcięcia limitem. */
export interface ListExportResponse<TRow> {
  records: TRow[];
  total: number;
  truncated: boolean;
  /** Etykiety odwołań (`{ [slug pola]: { [id]: nazwa } }`) wszystkich wyeksportowanych wierszy. */
  extRefLabels?: Record<string, Record<string, string | null>>;
}

export interface RunListCsvExportArgs<TRow> {
  /**
   * Id kolumn pliku (widoczne kolumny z ekranu, w jego kolejności) — USTALONE RAZ w chwili
   * kliknięcia i przekazywane zarówno do żądania, jak i do składania pliku. Dzięki temu serwer
   * zwraca dokładnie te kolumny, które trafią do pliku, nawet gdy ekran zmieni swoją listę
   * kolumn w trakcie pobierania.
   */
  columnIds: string[];
  /** Pobiera wiersze widoku tych kolumn; `ids` = tylko zaznaczone (brak → cały widok). */
  fetchRows: (ids: string[] | undefined, columnIds: string[]) => Promise<ListExportResponse<TRow>>;
  /** Kolumny pliku (z odpowiedzią w ręku — np. etykiety odwołań dołączone do odpowiedzi). */
  buildColumns: (response: ListExportResponse<TRow>, columnIds: string[]) => ExportColumn<TRow>[];
  /** Podstawa nazwy pliku (bez rodzaju, daty, identyfikatora i rozszerzenia — `exportFilename`). */
  filename: string;
  ids?: string[];
  /** Wynik kompletny — liczba wyeksportowanych rekordów. */
  onDone: (count: number) => void;
  /** Wynik obcięty limitem — ile rekordów w pliku z ilu w widoku. */
  onTruncated: (count: number, total: number) => void;
  /** Widok bez rekordów — plik nie powstaje. */
  onEmpty: () => void;
  /** Tytuł nad tabelą w pliku (gotowy tekst z datą pobrania); brak → bez wiersza tytułu. */
  heading?: string;
  /** Reguły formatowania warunkowego z układu listy (kolory komórek w pliku jak na ekranie). */
  formattingRules?: readonly FormattingRule[];
  /** Zapis pliku (przeglądarka: `downloadFile`). */
  download: (filename: string, file: Blob) => void;
}

/** Rodzaj eksportu w nazwie pliku: cały widok listy albo zaznaczone rekordy. */
export type ExportKind = 'lista' | 'zaznaczone';

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Podstawa nazwy bez znaków diakrytycznych („ł" nie rozkłada się w NFD — osobno). */
const asciiBase = (base: string) =>
  base.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l').replace(/Ł/g, 'L');

/**
 * Nazwa pliku eksportu, UNIKALNA przy każdym pobraniu:
 * `<podstawa>-<rodzaj>-RRRR-MM-DD-GG-MM-SS-<id>.xlsx`. Data i godzina w czasie lokalnym
 * użytkownika (nie UTC — eksport tuż po północy ma dzisiejszą datę), `<id>` = 8 znaków hex
 * losowych, więc dwa eksporty w tej samej sekundzie (kolejne kliknięcia, kilka kart lub sesji)
 * nie nadpisują się w folderze pobranych. Podstawa bez polskich znaków — część programów
 * (poczta, archiwizery, starsze Excele) psuje je w nazwach plików.
 */
export function exportFilename(
  base: string,
  kind: ExportKind,
  now: Date = new Date(),
  id: string = crypto.randomUUID().replace(/-/g, '').slice(0, 8),
): string {
  const date = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  const time = `${pad2(now.getHours())}-${pad2(now.getMinutes())}-${pad2(now.getSeconds())}`;
  return `${asciiBase(base)}-${kind}-${date}-${time}-${id}.xlsx`;
}

export async function runListCsvExport<TRow>(args: RunListCsvExportArgs<TRow>): Promise<void> {
  const response = await args.fetchRows(args.ids, args.columnIds);
  if (response.records.length === 0) {
    args.onEmpty();
    return;
  }
  const columns = args.buildColumns(response, args.columnIds);
  const file = await buildXlsxBlob({
    columns,
    rows: response.records,
    title: args.filename,
    formattingRules: args.formattingRules,
    heading: args.heading,
  });
  args.download(exportFilename(args.filename, args.ids ? 'zaznaczone' : 'lista'), file);
  if (response.truncated) args.onTruncated(response.records.length, response.total);
  else args.onDone(response.records.length);
}

/** Pobranie pliku w przeglądarce (tymczasowy link do obiektu Blob). */
export function downloadFile(filename: string, file: Blob): void {
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
